import api from './base';

export const coursesApi = {
  getAll: (params = {}) => {
    let url = '/courses/courses?';
    if (params.search) url += `search=${params.search}&`;
    if (params.category) url += `category=${params.category}&`;
    if (params.minPrice) url += `min_price=${params.minPrice}&`;
    if (params.maxPrice) url += `max_price=${params.maxPrice}&`;
    if (params.ordering) url += `ordering=${params.ordering}&`;
    if (params.page) url += `page=${params.page}&`;
    if (params.pageSize) url += `page_size=${params.pageSize}&`;
    return api.get(url);
  },

  getById: (courseId) => api.get(`/courses/courses/${courseId}`),

  create: (courseData, token) =>
    api.post('/courses/courses', courseData, {
      headers: { Authorization: `Token ${token}` },
    }),

  update: (courseId, courseData, token) =>
    api.put(`/courses/courses/${courseId}`, courseData, {
      headers: { Authorization: `Token ${token}` },
    }),

  delete: (courseId, token) =>
    api.delete(`/courses/courses/${courseId}`, {
      headers: { Authorization: `Token ${token}` },
    }),

  enroll: (courseId, token) =>
    api.post(
      `/courses/courses/${courseId}/enroll`,
      {},
      { headers: { Authorization: `Token ${token}` } }
    ),

  unenroll: (courseId, token) =>
    api.post(
      `/courses/courses/${courseId}/unenroll`,
      {},
      { headers: { Authorization: `Token ${token}` } }
    ),

  getModules: (courseId) => api.get(`/courses/courses/${courseId}/modules`),

  getModule: (courseId, moduleId) =>
    api.get(`/courses/courses/${courseId}/modules/${moduleId}`),

  createModule: (courseId, moduleData, token) =>
    api.post(`/courses/courses/${courseId}/modules`, moduleData, {
      headers: { Authorization: `Token ${token}` },
    }),

  updateModule: (courseId, moduleId, moduleData, token) =>
    api.put(`/courses/courses/${courseId}/modules/${moduleId}`, moduleData, {
      headers: { Authorization: `Token ${token}` },
    }),

  deleteModule: (courseId, moduleId, token) =>
    api.delete(`/courses/courses/${courseId}/modules/${moduleId}`, {
      headers: { Authorization: `Token ${token}` },
    }),

  getEnrolledCourses: (token) =>
    api.get('/courses/enrolled-courses', {
      headers: { Authorization: `Token ${token}` },
    }),

  getCreatedCourses: (token) =>
    api.get('/courses/courses?mentor=current', {
      headers: { Authorization: `Token ${token}` },
    }),

  getProgress: (courseId, token) =>
    api.get(`/courses/courses/${courseId}/progress`, {
      headers: { Authorization: `Token ${token}` },
    }),

  submitAssignment: (courseId, assignmentId, data, token) =>
    api.post(`/courses/courses/${courseId}/assignments/${assignmentId}/submit`, data, {
      headers: { Authorization: `Token ${token}` },
    }),

  // Categories
  getCategories: () => api.get('/courses/categories'),

  // Recommendations
  getRecommendations: (token) =>
    api.get('/courses/recommendations', {
      headers: { Authorization: `Token ${token}` },
    }),

  // Lessons
  getLesson: (lessonId, token) =>
    api.get(`/courses/lessons/${lessonId}`, {
      headers: { Authorization: `Token ${token}` },
    }),

  getLessonAssignments: (lessonId, token) =>
    api.get(`/courses/lessons/${lessonId}/assignments`, {
      headers: { Authorization: `Token ${token}` },
    }),

  createLesson: (lessonData, token) =>
    api.post('/courses/lessons', lessonData, {
      headers: { Authorization: `Token ${token}` },
    }),

  updateLesson: (lessonId, lessonData, token) =>
    api.patch(`/courses/lessons/${lessonId}`, lessonData, {
      headers: { Authorization: `Token ${token}` },
    }),

  deleteLesson: (lessonId, token) =>
    api.delete(`/courses/lessons/${lessonId}`, {
      headers: { Authorization: `Token ${token}` },
    }),

  completeLesson: (lessonId, token) =>
    api.post(`/courses/lessons/${lessonId}/complete`, {}, {
      headers: { Authorization: `Token ${token}` },
    }),

  // Assignments
  getAssignment: (assignmentId, token) =>
    api.get(`/courses/assignments/${assignmentId}`, {
      headers: { Authorization: `Token ${token}` },
    }),

  getAssignmentStatus: (assignmentId, token) =>
    api.get(`/courses/assignments/${assignmentId}/status`, {
      headers: { Authorization: `Token ${token}` },
    }),

  getAssignmentFeedback: (assignmentId, token) =>
    api.get(`/courses/assignments/${assignmentId}/feedback/`, {
      headers: { Authorization: `Token ${token}` },
    }),

  createAssignment: (assignmentData, token) =>
    api.post('/courses/standalone-assignments/', assignmentData, {
      headers: { Authorization: `Token ${token}` },
    }),

  updateAssignment: (assignmentId, assignmentData, token) =>
    api.patch(`/courses/assignments/${assignmentId}`, assignmentData, {
      headers: { Authorization: `Token ${token}` },
    }),

  deleteAssignment: (assignmentId, token) =>
    api.delete(`/courses/assignments/${assignmentId}`, {
      headers: { Authorization: `Token ${token}` },
    }),

  getUserAssignments: (token) =>
    api.get('/courses/user-assignments/', {
      headers: { Authorization: `Token ${token}` },
    }),

  assignTaskToUser: (assignmentData, token) =>
    api.post('/courses/user-assignments/', assignmentData, {
      headers: { Authorization: `Token ${token}` },
    }),

  getCreatedAssignments: (token) =>
    api.get('/courses/standalone-assignments/', {
      headers: { Authorization: `Token ${token}` },
    }),

  // Submissions
  createSubmission: (assignmentId, data, token) =>
    api.post('/courses/submissions', {
      assignment: assignmentId,
      ...data
    }, {
      headers: { Authorization: `Token ${token}` },
    }),

  getAssignmentSubmissions: (assignmentId, token) =>
    api.get(`/courses/assignments/${assignmentId}/submissions/`, {
      headers: { Authorization: `Token ${token}` },
    }),

  updateSubmission: (submissionId, data, token) =>
    api.patch(`/courses/submissions/${submissionId}/`, data, {
      headers: { Authorization: `Token ${token}` },
    }),

  updateSubmissionFeedback: (submissionId, data, token) =>
    api.patch(`/courses/submissions/${submissionId}/review`, data, {
      headers: { Authorization: `Token ${token}` },
    }),

  // Enrollments
  getEnrollments: (token) =>
    api.get('/courses/enrollments/list', {
      headers: { Authorization: `Token ${token}` },
    }),

  // Reviews and Ratings
  getReviews: (courseId) =>
    api.get(`/courses/reviews?course_id=${courseId}`),

  createReview: (courseId, content, token) =>
    api.post('/courses/reviews', {
      course: courseId,
      content
    }, {
      headers: { Authorization: `Token ${token}` },
    }),

  getRatings: (courseId) =>
    api.get(`/courses/ratings?course_id=${courseId}`),

  createRating: (courseId, rating, token) =>
    api.post('/courses/ratings', {
      course: courseId,
      rating
    }, {
      headers: { Authorization: `Token ${token}` },
    }),

  // Comments
  getComments: (courseId, token) =>
    api.get('/courses/comments', {
      params: {
        content_type: 'course',
        object_id: courseId
      },
      headers: { Authorization: `Token ${token}` }
    }),

  createComment: (courseId, content, token) =>
    api.post('/courses/comments/create', {
      content_type: 'course',
      object_id: courseId,
      content
    }, {
      headers: { Authorization: `Token ${token}` }
    }),

  // Student Environments
  getStudentEnvironmentStatus: (environmentId, token) =>
    api.get(`/courses/student-environments/${environmentId}/`, {
      headers: { Authorization: `Token ${token}` }
    }),

  recreateEnvironment: (environmentId, token) =>
    api.post(`/courses/student-environments/${environmentId}/recreate/`, {}, {
      headers: { Authorization: `Token ${token}` }
    }),

  destroyEnvironment: (environmentId, token) =>
    api.delete(`/courses/student-environments/${environmentId}/`, {
      headers: { Authorization: `Token ${token}` }
    }),

  // Assignment attachments
  attachSandboxToAssignment: (assignmentId, sandboxId, templateId, token) =>
    api.post(`/courses/assignments/${assignmentId}/attach-sandbox/`,
      { sandbox_id: sandboxId, template_id: templateId },
      { headers: { Authorization: `Token ${token}` } }
    ),

  // Course attachments
  attachAssistantToCourse: (courseId, tutorId, token) =>
    api.post(`/courses/courses/${courseId}/attach-assistant/`,
      { tutor_id: tutorId },
      { headers: { Authorization: `Token ${token}` } }
    ),
};
